import React from 'react';
import { Grid } from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import { BaseCard } from '../common/BaseCard';
import { DataItem } from '../common/DataItem';
import { formatDateSafe, formatNaturezaJuridica, getCnaeDisplay } from '../utils/formatters';
import { ILocalizacaoPF, ILocalizacaoPJ } from '../../../../../../application/models/dossie';

export const InformacoesPrincipaisCard = ({
  localizacao,
  isPF,
  docFormatado,
}: {
  localizacao: ILocalizacaoPF | ILocalizacaoPJ | null;
  isPF: boolean;
  docFormatado: string;
}) => {
  if (!localizacao) return null;

  const pfLoc = isPF ? (localizacao as ILocalizacaoPF) : null;
  const pjLoc = !isPF ? (localizacao as ILocalizacaoPJ) : null;

  const title = isPF ? 'Identificação de Pessoa Física' : 'Identificação de Pessoa Jurídica';
  const icon = isPF ? <PersonOutlineIcon /> : <BusinessOutlinedIcon />;

  return (
    <BaseCard title={title} icon={icon}>
      <Grid container spacing={2} className="info-principais-grid">
        {isPF ? (
          <>
            {/* Linha 1: Nome com bastante espaço horizontal + CPF */}
            <Grid item xs={12} md={8} lg={8.5} className="info-pf-nome">
              <DataItem label="Nome" value={pfLoc?.dadosPessoa?.Nome} />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3.5} className="info-pf-cpf">
              <DataItem label="CPF" value={docFormatado} preserveCase={true} />
            </Grid>

            {/* Linha 2: Nascimento, Idade, Mãe e Pai */}
            <Grid item xs={12} sm={6} md={3} lg={2.5} className="info-pf-nasc">
              <DataItem
                label="Data de Nascimento"
                value={formatDateSafe(pfLoc?.dadosCadastrais?.DataNascimento)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2} lg={1.5} className="info-pf-idade">
              <DataItem
                label="Idade"
                value={pfLoc?.dadosCadastrais?.Idade ? `${pfLoc.dadosCadastrais.Idade} Anos` : '-'}
                preserveCase={true}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={pfLoc?.dadosCadastrais?.NomePai ? 6 : 12}
              md={pfLoc?.dadosCadastrais?.NomePai ? 3.5 : 7}
              lg={pfLoc?.dadosCadastrais?.NomePai ? 4 : 8}
              className="info-pf-mae"
            >
              <DataItem label="Nome da Mãe" value={pfLoc?.dadosCadastrais?.NomeMae} />
            </Grid>

            {pfLoc?.dadosCadastrais?.NomePai && (
              <Grid item xs={12} sm={6} md={3.5} lg={4} className="info-pf-pai">
                <DataItem label="Nome do Pai" value={pfLoc.dadosCadastrais.NomePai} />
              </Grid>
            )}
          </>
        ) : (
          <>
            {/* Se houver Nome Fantasia */}
            {pjLoc?.dadosPessoa?.NomeFantasia ? (
              <>
                {/* Linha 1: Razão Social + CNPJ */}
                <Grid item xs={12} md={6} className="info-pj-half">
                  <DataItem label="Razão Social" value={pjLoc?.dadosPessoa?.RazaoSocial} />
                </Grid>
                <Grid item xs={12} md={6} className="info-pj-half">
                  <DataItem label="CNPJ" value={docFormatado} preserveCase={true} />
                </Grid>

                {/* Linha 2: Nome Fantasia + Data de Abertura */}
                <Grid item xs={12} md={6} className="info-pj-half">
                  <DataItem label="Nome Fantasia" value={pjLoc.dadosPessoa.NomeFantasia} />
                </Grid>
                <Grid item xs={12} md={6} className="info-pj-half">
                  <DataItem
                    label="Data de Abertura"
                    value={formatDateSafe(pjLoc?.dadosCadastrais?.DataAbertura)}
                  />
                </Grid>

                {/* Linha 3: Natureza Jurídica + Tipo de Empresa */}
                <Grid item xs={12} md={6} className="info-pj-half">
                  <DataItem
                    label="Natureza Jurídica"
                    value={
                      pjLoc?.dadosCadastrais?.NaturezaJuridica
                        ? `${formatNaturezaJuridica(pjLoc.dadosCadastrais.NaturezaJuridica)} - ${pjLoc.dadosCadastrais.DescricaoNaturezaJuridica || ''}`
                        : pjLoc?.dadosCadastrais?.DescricaoNaturezaJuridica
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6} className="info-pj-half">
                  <DataItem
                    label="Tipo de Empresa"
                    value={pjLoc?.dadosCadastrais?.DescricaoTipoEmpresa || '-'}
                  />
                </Grid>

                {/* Linha 4: CNAE Principal */}
                <Grid item xs={12} className="info-pj-full">
                  <DataItem
                    label="CNAE Principal"
                    value={getCnaeDisplay(
                      pjLoc?.dadosCadastrais?.CnaePrincipal,
                      pjLoc?.dadosCadastrais?.DescricaoCnaePrincipal
                    )}
                  />
                </Grid>
              </>
            ) : (
              <>
                {/* Linha 1: Razão Social + CNPJ */}
                <Grid item xs={12} md={6} className="info-pj-half">
                  <DataItem label="Razão Social" value={pjLoc?.dadosPessoa?.RazaoSocial} />
                </Grid>
                <Grid item xs={12} md={6} className="info-pj-half">
                  <DataItem label="CNPJ" value={docFormatado} preserveCase={true} />
                </Grid>

                {/* Linha 2: Data de Abertura + Natureza Jurídica */}
                <Grid item xs={12} md={6} className="info-pj-half">
                  <DataItem
                    label="Data de Abertura"
                    value={formatDateSafe(pjLoc?.dadosCadastrais?.DataAbertura)}
                  />
                </Grid>
                <Grid item xs={12} md={6} className="info-pj-half">
                  <DataItem
                    label="Natureza Jurídica"
                    value={
                      pjLoc?.dadosCadastrais?.NaturezaJuridica
                        ? `${formatNaturezaJuridica(pjLoc.dadosCadastrais.NaturezaJuridica)} - ${pjLoc.dadosCadastrais.DescricaoNaturezaJuridica || ''}`
                        : pjLoc?.dadosCadastrais?.DescricaoNaturezaJuridica
                    }
                  />
                </Grid>

                {/* Linha 3: CNAE Principal + Tipo de Empresa */}
                <Grid item xs={12} md={6} className="info-pj-half">
                  <DataItem
                    label="CNAE Principal"
                    value={getCnaeDisplay(
                      pjLoc?.dadosCadastrais?.CnaePrincipal,
                      pjLoc?.dadosCadastrais?.DescricaoCnaePrincipal
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6} className="info-pj-half">
                  <DataItem
                    label="Tipo de Empresa"
                    value={pjLoc?.dadosCadastrais?.DescricaoTipoEmpresa || '-'}
                  />
                </Grid>
              </>
            )}
          </>
        )}
      </Grid>
    </BaseCard>
  );
};
